import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useCrud, Card, Spinner, EmptyState, PageHeader, Button, Table, Field, inputClass, type CrudConfig } from '@/components';
import { formatDate, truncate, StatusBadge, ModalShell, saveJson, SaveStatus } from './shared';

interface Row {
  id: string;
  title: string;
  slug: string;
  department: string;
  location: string;
  type: string;
  description: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const LIST_ENDPOINT = '/careers/admin';
const ENDPOINT = '/careers';

export function CareersAdmin() {
  const { rows, loading, error, refresh, remove } = useCrud<Row, CrudConfig<Row>>({ endpoint: LIST_ENDPOINT });
  const [editing, setEditing] = useState<Row | null>(null);

  const handleDelete = (r: Row) => {
    if (window.confirm(`Delete job "${r.title || r.slug || r.id}"?`)) void remove(String(r.id));
  };

  return (
    <div>
      <PageHeader
        title="Careers"
        subtitle="Job openings listed on the careers page"
        actions={<Button onClick={() => setEditing({} as Row)}>New Job</Button>}
      />
      {loading ? (
        <Spinner />
      ) : error ? (
        <Card>
          <p className="text-sm text-red-600">{error}</p>
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <Table<Row>
          rows={rows}
          onEdit={setEditing}
          onDelete={handleDelete}
          columns={[
            { key: 'title', header: 'Title', render: (r) => <span className="font-medium">{truncate(r.title)}</span> },
            { key: 'department', header: 'Department' },
            { key: 'location', header: 'Location' },
            { key: 'type', header: 'Type' },
            { key: 'active', header: 'Status', render: (r) => <StatusBadge on={r.active} /> },
            { key: 'updatedAt', header: 'Updated', render: (r) => formatDate(r.updatedAt ?? r.createdAt) },
          ]}
        />
      )}
      {editing && <CareerFormModal row={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </div>
  );
}

function CareerFormModal({ row, onClose, onSaved }: { row: Row; onClose: () => void; onSaved: () => void }) {
  const isEdit = Boolean(row.id);
  const [form, setForm] = useState<Record<string, string | boolean>>(() => ({
    title: String(row.title ?? ''),
    slug: String(row.slug ?? ''),
    department: String(row.department ?? ''),
    location: String(row.location ?? ''),
    type: String(row.type ?? ''),
    description: String(row.description ?? ''),
    active: Boolean(row.active),
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const set = (key: string) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const setChecked = (key: string) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.checked }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await saveJson(isEdit ? 'PUT' : 'POST', `${ENDPOINT}${isEdit ? `/${row.id}` : ''}`, { ...form });
      setSuccess(true);
      onSaved();
    } catch (err) {
      setError((err as Error).message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={`${isEdit ? 'Edit' : 'New'} Job Opening`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Title">
            <input className={inputClass} value={String(form.title ?? '')} onChange={set('title')} required />
          </Field>
          <Field label="Slug">
            <input className={inputClass} value={String(form.slug ?? '')} onChange={set('slug')} placeholder="frontend-developer" />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Department">
            <input className={inputClass} value={String(form.department ?? '')} onChange={set('department')} placeholder="Engineering" />
          </Field>
          <Field label="Location">
            <input className={inputClass} value={String(form.location ?? '')} onChange={set('location')} placeholder="Nairobi, Kenya" />
          </Field>
          <Field label="Type">
            <input className={inputClass} value={String(form.type ?? '')} onChange={set('type')} placeholder="Full-time" />
          </Field>
        </div>
        <Field label="Description">
          <textarea className={inputClass} rows={6} value={String(form.description ?? '')} onChange={set('description')} />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={Boolean(form.active)} onChange={setChecked('active')} className="h-4 w-4 rounded border-slate-300" />
          Active
        </label>
        <SaveStatus error={error} success={success} />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" className={saving ? 'opacity-60' : ''}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Job'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

export default CareersAdmin;